/**
 * XML escaping helpers for prompt templates.
 *
 * CRM-derived values (field titles, enum option labels) are operator/integration data
 * interpolated into the XML-structured prompt. A title like `Budget "USD" <b>` would
 * otherwise break the prompt's XML framing or smuggle instructions to the LLM, so all
 * such values must be escaped before interpolation.
 */

/** Escape the XML-significant characters for text content (`&`, `<`, `>`). */
export function escapeXmlText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Escape for use inside a double-quoted XML attribute value (adds `"`). */
export function escapeXmlAttr(value: string): string {
  return escapeXmlText(value).replace(/"/g, '&quot;');
}

/**
 * Make a customer-authored string safe to carry inside a `---BEGIN/END …---`
 * delimited prompt block.
 *
 * XML escaping is not enough here: the block's framing is textual, not markup.
 * A newline ends the entry's own line and turns the remainder into prompt
 * prose, and a quoted `---END …---` reads as the end marker even mid-line. Both
 * are collapsed to something inert that still matches as data: whitespace to a
 * single space, any run of three or more hyphens to two.
 */
export function neutralizeListDelimiters(value: string): string {
  return value.replace(/\s+/g, ' ').replace(/-{3,}/g, '--').trim();
}
